import { MRT_ColumnDef } from 'mantine-react-table'
import { Avatar, Text, Tooltip } from '@mantine/core'
import { ReactNode } from 'react'
import { DealParams } from 'app/models/deal-params'
import getInitials from 'app/views/components/functions/getInitials'
import DealActions from './deal-details/deal-actions'
import StatusPill from '../common/pill-status'
import DealHoldIndicator from 'app/views/components/deal-hold-indicator'
import { formatDateToShow, formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'

function TableHead({ header }: { header: string }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {header}
    </Text>
  )
}

function TableRowText({ text }: { text: ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {text}
    </Text>
  )
}
export const columnDefs: MRT_ColumnDef<DealParams>[] = [
  {
    size: 275,
    accessorKey: 'dealName',
    header: 'Deal Name',
    Header: <TableHead header={'Deal Name'} />,
    Cell: ({ cell }) => {
      return <TableRowText text={cell.getValue() as string} />
    },
  },
  {
    size: 150,
    accessorKey: 'status',
    header: 'Status',
    Header: <TableHead header={'Status'} />,
    Cell: cell => {
      const aditionalStatus = cell.row.original.amendment
        ? cell.row.original.amendmentType ?? 'Amendments'
        : ''
      return (
        <StatusPill
          state={cell.row.original.status ?? 'Draft'}
          additionalState={aditionalStatus}
        />
      )
    },
  },
  {
    // Display-only SP hold indicator (deal-level). Mirrors the Facilities list
    // Alerts column; deals have no non-pro-rata indicator, so the hold is the
    // only alert here.
    accessorKey: 'alerts',
    header: 'Alerts',
    Header: <TableHead header={'Alerts'} />,
    enableSorting: false,
    size: 100,
    Cell: ({ row }) => (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <DealHoldIndicator deal={row.original} />
      </div>
    ),
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    Header: <TableHead header={'Amount'} />,
    filterVariant: 'range',
    Cell: ({ cell, row }: any) => {
      return (
        <TableRowText
          text={formatNumberToCurrency(cell.getValue(), row.original.currency ?? 'USD')}
        />
      )
    },
  },
  {
    accessorKey: 'utilizedAmount',
    header: 'Utilized Amount',
    Header: <TableHead header={'Utilized Amount'} />,
    filterVariant: 'range',
    Cell: ({ cell, row }: any) => (
      <TableRowText
        text={formatNumberToCurrency(cell.getValue(), row.original.currency ?? 'USD')}
      />
    ),
  },
  {
    size: 125,
    accessorKey: 'startDate',
    header: 'Start Date',
    Header: <TableHead header={'Start Date'} />,
    accessorFn: row => {
      return stringToDate(row.startDate);
    },
    mantineFilterTextInputProps: {
      placeholder: 'Date',
    },
    filterFn: 'lessThanOrEqualTo',
    filterVariant: 'date',
    sortingFn: 'datetime',
    Cell: ({ cell }) => (
      <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
    ), //render Date as a string
  },
  {
    size: 125,
    accessorKey: 'maturity',
    header: 'Maturity Date',
    Header: <TableHead header={'Maturity Date'} />,
    accessorFn: row => {
      return stringToDate(row.maturity);
    },
    mantineFilterTextInputProps: {
      placeholder: 'Date',
    },
    filterFn: 'greaterThanOrEqualTo',
    filterVariant: 'date',
    sortingFn: 'datetime',
    Cell: ({ cell }) => (
      <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
    ), //render Date as a string
  },
  {
    size: 100,
    accessorFn: row => { return row.borrowerNames ? row.borrowerNames[0] : '' },
    accessorKey: 'borrowerNames',
    header: 'Borrowers',
    Header: <TableHead header={'Borrowers'} />,
    Cell: ({ cell }) => {
      return (
        <Tooltip label={cell.getValue() as string}>
          <Avatar radius="xl" color="cyan" size="lg">
            {getInitials(cell.getValue<string>())}
          </Avatar>
        </Tooltip>
      )
    },
  },
  {
    size: 100,
    accessorKey: 'adminEntityName',
    header: 'Admin',
    Header: <TableHead header={'Admin'} />,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      if (Array.isArray(value)) {
        return (
          <Avatar.Group spacing="sm">
            {value.map((adminEntity, i) => (
              <Tooltip key={i} label={adminEntity}>
                <Avatar radius="xl" color="cyan" size="lg">
                  {getInitials(adminEntity)}
                </Avatar>
              </Tooltip>
            ))}
          </Avatar.Group>
        );
      }
    },
  },
  {
    accessorKey: 'id',
    header: 'Actions',
    Header: <TableHead header={'Actions'} />,
    enableColumnActions: false,
    enableColumnFilter: false,
    Cell: cell => <DealActions row={cell.row.original} />,
  },
  //System ID is a hidden column and only used for Export to CSV
  {
    accessorKey: 'systemId',
    header: 'System ID',
    enableHiding: false,   // prevents toggling
    enableColumnFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    Cell: () => null,
    Header: () => null,
  }
]
